/**
 * Classification Proposals Report
 *
 * Shows all item classification proposals in the current workspace.
 * Category proposals and altitude proposals are tracked separately for each item.
 *
 * Proposal lifecycle statuses:
 * - active     — open, awaiting human review
 * - accepted   — human confirmed the proposal as-is
 * - overridden — human reviewed but entered different values
 * - superseded — replaced by a newer agent proposal before human action
 * - rejected   — human dismissed without setting a classification
 *
 * Route: /prioritization/settings/classification-report
 */

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  RefreshCw,
  Bot,
  UserCircle,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRightLeft,
  Ban,
  Loader2,
  ExternalLink,
  Tag,
  ArrowUpCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import type { ItemClassificationProposal } from '../../../types/prioritization/itemClassification';
import { PROPOSAL_STATUS_LABELS, proposerLabel, rowToClassificationProposal } from '../../../types/prioritization/itemClassification';
import type { ItemLanguageType } from '../../../types/prioritization/lifecycle';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../../lib/itemCategoryColors';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';

// ── Types ──────────────────────────────────────────────────────────────────────

type ProposalStatus = ItemClassificationProposal['status'];

interface ProposalRow extends ItemClassificationProposal {
  itemTitle?: string;
}

interface SummaryStats {
  total: number;
  active: number;
  accepted: number;
  overridden: number;
  superseded: number;
  rejected: number;
  byCategory: Record<string, number>;
  byAltitude: Record<number, number>;
}

// ── Display helpers ────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<ItemLanguageType, string> = {
  problem: 'Problem',
  opportunity: 'Opportunity',
  risk: 'Risk',
  solution: 'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

function CategoryChip({ category }: { category: string }) {
  const label = CATEGORY_LABELS[category as ItemLanguageType] ?? category;
  const { bg, text, border } = CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${bg} ${text} ${border}`}
    >
      <Tag className="h-3 w-3" />
      {label}
    </span>
  );
}

const STATUS_CONFIG: Record<
  ProposalStatus,
  { icon: React.ReactNode; bg: string; text: string; label: string }
> = {
  active: {
    icon: <Clock className="h-3.5 w-3.5" />,
    bg: 'bg-[var(--mustard-dim)]',
    text: 'text-[var(--mustard)]',
    label: PROPOSAL_STATUS_LABELS.active,
  },
  accepted: {
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    bg: 'bg-[var(--green-dim)]',
    text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    label: PROPOSAL_STATUS_LABELS.accepted,
  },
  overridden: {
    icon: <ArrowRightLeft className="h-3.5 w-3.5" />,
    bg: 'bg-[var(--slate-dim)]',
    text: 'text-[var(--text-sec)]',
    label: PROPOSAL_STATUS_LABELS.overridden,
  },
  superseded: {
    icon: <RefreshCw className="h-3.5 w-3.5" />,
    bg: 'bg-[var(--hover-tint)]',
    text: 'text-[var(--text-ter)]',
    label: PROPOSAL_STATUS_LABELS.superseded,
  },
  rejected: {
    icon: <Ban className="h-3.5 w-3.5" />,
    bg: 'bg-[var(--coral-dim)]',
    text: 'text-[var(--coral)]',
    label: PROPOSAL_STATUS_LABELS.rejected,
  },
};

function StatusBadge({ status }: { status: ProposalStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.superseded;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function AltitudeBadge({ level, category }: { level: number; category?: string | null }) {
  const altitudeText = useAltitudeText();
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)]">
      <ArrowUpCircle className="h-3 w-3" />
      {altitudeText.format(level, category) ?? `Altitude ${level}`}
    </span>
  );
}

// ── Summary stats card ─────────────────────────────────────────────────────────

function StatsRow({
  label,
  value,
  icon,
  colorClass,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  colorClass: string;
}) {
  return (
    <div className={`flex items-center justify-between py-2 px-3  ${colorClass}`}>
      <span className="flex items-center gap-2 text-xs font-medium">
        {icon}
        {label}
      </span>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function SummaryPanel({ stats }: { stats: SummaryStats }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <div className="card space-y-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">
          By outcome
        </p>
        <StatsRow
          label="Pending review"
          value={stats.active}
          icon={<Clock className="h-3.5 w-3.5 text-[var(--mustard)]" />}
          colorClass="bg-[var(--mustard-dim)]"
        />
        <StatsRow
          label="Accepted"
          value={stats.accepted}
          icon={<CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />}
          colorClass="bg-[var(--green-dim)]"
        />
        <StatsRow
          label="Overridden"
          value={stats.overridden}
          icon={<ArrowRightLeft className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
          colorClass="bg-[var(--slate-dim)]"
        />
        <StatsRow
          label="Superseded"
          value={stats.superseded}
          icon={<RefreshCw className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
          colorClass="bg-[var(--surface)]"
        />
        <StatsRow
          label="Dismissed"
          value={stats.rejected}
          icon={<Ban className="h-3.5 w-3.5 text-[var(--coral)]" />}
          colorClass="bg-[var(--coral-dim)]"
        />
      </div>

      <div className="card space-y-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">
          Category breakdown
        </p>
        {Object.entries(stats.byCategory).length === 0 ? (
          <p className="text-xs text-[var(--text-ter)] italic">No proposals yet</p>
        ) : (
          Object.entries(stats.byCategory)
            .sort(([, a], [, b]) => b - a)
            .map(([cat, count]) => (
              <div key={cat} className="flex items-center justify-between text-xs">
                <CategoryChip category={cat} />
                <span className="font-medium tabular-nums">{count}</span>
              </div>
            ))
        )}
      </div>

      <div className="card space-y-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">
          Altitude breakdown
        </p>
        {Object.entries(stats.byAltitude).length === 0 ? (
          <p className="text-xs text-[var(--text-ter)] italic">No proposals yet</p>
        ) : (
          [1, 2, 3, 4, 5].map((lvl) =>
            stats.byAltitude[lvl] ? (
              <div key={lvl} className="flex items-center justify-between text-xs">
                <AltitudeBadge level={lvl} />
                <span className="font-medium tabular-nums">{stats.byAltitude[lvl]}</span>
              </div>
            ) : null,
          )
        )}
      </div>
    </div>
  );
}

// ── Proposal row ───────────────────────────────────────────────────────────────

function ProposalTableRow({ proposal }: { proposal: ProposalRow }) {
  const [expanded, setExpanded] = useState(false);
  const fields = proposal.classificationFields;

  return (
    <>
      <tr className="border-b border-[var(--rule)] hover:bg-[var(--surface)] transition-colors">
        {/* Item */}
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/prioritization/items/${proposal.itemId}`}
              className="text-xs font-medium text-[var(--text-ter)] hover:underline truncate max-w-[180px]"
              title={proposal.itemTitle}
            >
              {proposal.itemTitle ?? proposal.itemId.slice(0, 12) + '…'}
            </Link>
            <ExternalLink className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          </div>
        </td>

        {/* Category proposal */}
        <td className="py-2.5 px-3">
          <CategoryChip category={fields.category} />
        </td>

        {/* Altitude proposal */}
        <td className="py-2.5 px-3">
          <AltitudeBadge level={fields.altitudeLevel} category={fields.category} />
        </td>

        {/* Status */}
        <td className="py-2.5 px-3">
          <StatusBadge status={proposal.status} />
        </td>

        {/* Proposer */}
        <td className="py-2.5 px-3">
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)]">
            {proposal.proposedBy === 'agent' ? (
              <Bot className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            ) : (
              <UserCircle className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            )}
            {proposerLabel(proposal)}
          </span>
        </td>

        {/* Confidence */}
        <td className="py-2.5 px-3">
          {fields.confidence ? (
            <span
              className={`text-xs font-medium ${
                fields.confidence === 'high'
                  ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                  : fields.confidence === 'medium'
                  ? 'text-[var(--mustard)]'
                  : 'text-[var(--coral)]'
              }`}
            >
              {fields.confidence}
            </span>
          ) : (
            <span className="text-xs text-[var(--text-ter)]">—</span>
          )}
        </td>

        {/* Date */}
        <td className="py-2.5 px-3 text-xs text-[var(--text-ter)] whitespace-nowrap">
          {new Date(proposal.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </td>

        {/* Expand toggle */}
        <td className="py-2.5 px-2">
          {fields.rationale ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="p-1 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] transition-colors"
              title={expanded ? 'Collapse' : 'Show rationale'}
            >
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
          ) : null}
        </td>
      </tr>

      {/* Rationale expansion row */}
      {expanded && fields.rationale && (
        <tr className="bg-[var(--slate-dim)] border-b border-[var(--slate)]">
          <td colSpan={8} className="px-4 py-2">
            <p className="text-xs text-[var(--text-sec)] leading-relaxed">
              <strong>Rationale:</strong> {fields.rationale}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Filter bar ─────────────────────────────────────────────────────────────────

const ALL_STATUS_OPTIONS: { value: ProposalStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'overridden', label: 'Overridden' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'rejected', label: 'Dismissed' },
];

const ALL_PROPOSER_OPTIONS = [
  { value: 'all', label: 'All proposers' },
  { value: 'agent', label: 'Agent only' },
  { value: 'user', label: 'Human only' },
];

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ClassificationProposalsReport() {
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'all'>('all');
  const [proposerFilter, setProposerFilter] = useState<'all' | 'agent' | 'user'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const workspaceId = getCurrentWorkspaceId();

  const fetchProposals = async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !workspaceId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Join with items to get title
      const { data, error: fetchErr } = await supabase
        .from('item_classification_proposals')
        .select(`
          *,
          items:item_id ( title )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(1000);

      if (fetchErr) {
        setError(fetchErr.message);
        return;
      }

      const rows: ProposalRow[] = (data ?? []).map((row) => {
        const base = rowToClassificationProposal(row as Record<string, unknown>);
        return {
          ...base,
          itemTitle: (row as { items?: { title?: string } }).items?.title ?? undefined,
        };
      });

      setProposals(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProposals();
  }, [workspaceId]);

  // Derive all unique categories for filter dropdown
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    proposals.forEach((p) => cats.add(p.classificationFields.category));
    return Array.from(cats).sort();
  }, [proposals]);

  // Apply filters
  const filtered = useMemo(() => {
    return proposals.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (proposerFilter !== 'all' && p.proposedBy !== proposerFilter) return false;
      if (categoryFilter !== 'all' && p.classificationFields.category !== categoryFilter) return false;
      return true;
    });
  }, [proposals, statusFilter, proposerFilter, categoryFilter]);

  // Compute summary stats from all proposals (unfiltered)
  const stats = useMemo((): SummaryStats => {
    const byCategory: Record<string, number> = {};
    const byAltitude: Record<number, number> = {};

    proposals.forEach((p) => {
      const cat = p.classificationFields.category;
      byCategory[cat] = (byCategory[cat] ?? 0) + 1;
      const alt = p.classificationFields.altitudeLevel;
      byAltitude[alt] = (byAltitude[alt] ?? 0) + 1;
    });

    return {
      total: proposals.length,
      active: proposals.filter((p) => p.status === 'active').length,
      accepted: proposals.filter((p) => p.status === 'accepted').length,
      overridden: proposals.filter((p) => p.status === 'overridden').length,
      superseded: proposals.filter((p) => p.status === 'superseded').length,
      rejected: proposals.filter((p) => p.status === 'rejected').length,
      byCategory,
      byAltitude,
    };
  }, [proposals]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          icon={<BarChart3 className="h-4 w-4 text-[var(--text-sec)]" />}
          iconBg="bg-[var(--hover-tint)]"
          title="Classification Proposals Report"
          subtitle="Full history of all category and altitude classification proposals in this workspace — pending, accepted, overridden, and dismissed."
        />
        <button
          type="button"
          onClick={() => void fetchProposals()}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors mt-1 flex-shrink-0"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary stats */}
      <SummaryPanel stats={stats} />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProposalStatus | 'all')}
          className="text-xs border border-[var(--rule)] px-2.5 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {ALL_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={proposerFilter}
          onChange={(e) => setProposerFilter(e.target.value as typeof proposerFilter)}
          className="text-xs border border-[var(--rule)] px-2.5 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {ALL_PROPOSER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {allCategories.length > 0 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs border border-[var(--rule)] px-2.5 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All categories</option>
            {allCategories.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c as ItemLanguageType] ?? c}
              </option>
            ))}
          </select>
        )}

        {(statusFilter !== 'all' || proposerFilter !== 'all' || categoryFilter !== 'all') && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('all');
              setProposerFilter('all');
              setCategoryFilter('all');
            }}
            className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] underline"
          >
            Clear filters
          </button>
        )}

        <span className="text-xs text-[var(--text-ter)] ml-auto">
          {filtered.length} of {proposals.length} proposals
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      {/* Not configured */}
      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">Select a workspace to view its classification history.</p>
        </div>
      )}

      {/* Table */}
      {workspaceId && (
        <div className="card p-0 overflow-hidden">
          {loading && proposals.length === 0 ? (
            <div className="flex items-center gap-2 py-6 px-4 text-sm text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading proposals…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 px-4 text-center">
              <XCircle className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-ter)]">
                {proposals.length === 0
                  ? 'No classification proposals yet. Run the ATC agent on any item to get started.'
                  : 'No proposals match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--rule)] bg-[var(--surface)]">
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Item</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Category</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Altitude</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Status</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Proposer</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Confidence</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-[var(--text-ter)]">Date</th>
                    <th className="py-2.5 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((proposal) => (
                    <ProposalTableRow key={proposal.id} proposal={proposal} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="card bg-[var(--surface)] border-[var(--rule)] space-y-2">
        <p className="text-xs font-semibold text-[var(--text-sec)]">Proposal status glossary</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {(Object.keys(STATUS_CONFIG) as ProposalStatus[]).map((s) => (
            <div key={s} className="flex items-start gap-2 text-xs text-[var(--text-ter)]">
              <StatusBadge status={s} />
              <span className="mt-0.5">
                {s === 'active' && 'Open proposal awaiting human review.'}
                {s === 'accepted' && 'Human confirmed the proposal as-is.'}
                {s === 'overridden' && 'Human reviewed but entered different values.'}
                {s === 'superseded' && 'Replaced by a newer agent proposal before human action.'}
                {s === 'rejected' && 'Human dismissed the proposal without setting a classification.'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
